import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { widgetFont } from '../../widgetTypography'
import { useNavigate } from 'react-router-dom'
import { evaluateCondition } from '../../../../../../common/model/condition/evaluate'
import useTranslation from '../../../../hooks/useTranslation'
import AnalyticsCard from './AnalyticsCard'
import { KpiRow, formatOf } from './Kpi'
import { rankGroups } from './measures'
import { groupName } from './HorizontalBarWidget'
import useAnalyticsWidget from './useAnalyticsWidget'
import useEntityRows from './useEntityRows'
import useElementSize from './useElementSize'

/*
 * A compact table for comparing rows — top customers, recent invoices. Its rows
 * come from one of two places:
 *
 *     table: { groups: { value: 'sales', limit: 8, labels, emptyLabel }, … }
 *         one row per category of a grouped measure; columns are expressions
 *         evaluated per row — { value: 'sales' }, { value: { shareOf: 'sales' } }
 *
 *     table: { entity: 'invoice', filter: 'state:final', dateField: 'date', limit: 8, … }
 *         the newest records of a list in the period; columns read fields
 *
 *     columns: [{ id, label, kind: 'name' | value | field, format, align, hideBelow }]
 *         format    currency, integer, number, percent, date, datetime, relative, text,
 *                   status — with `statuses: [{ condition, pastDue, label, tone }]`, the
 *                   first that holds; `pastDue: 'paymentDate'` holds when that date is gone
 *         hideBelow the card width in px under which the column is dropped — the
 *                   name and the main figure stay, the detail goes
 *     sort       { column, direction } — the starting order; headers re-sort
 *     rowPath    a row opens {rowPath}/{id}
 *
 * It sorts only the rows it shows. A table of everything is the list page's
 * job; `path` puts a "View all" link in the header to go there.
 */

const Scroll = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
`

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    ${widgetFont.body}
    font-variant-numeric: tabular-nums;

    th, td {
        padding: 0.4rem 0.4rem;
        text-align: left;
        white-space: nowrap;
    }
    th:first-child, td:first-child { padding-left: 0.15rem; }
    th:last-child, td:last-child { padding-right: 0.15rem; }
    .num { text-align: right; }

    thead th {
        position: sticky;
        top: 0;
        z-index: 1;
        background: ${p => p.theme.card.background};
        ${widgetFont.label}
        font-weight: 500;
        color: ${p => p.theme.font.label};
        border-bottom: 1px solid ${p => p.theme.table.border};
    }
    tbody tr + tr td { border-top: 1px solid ${p => p.theme.table.border}; }
    tbody tr[data-clickable='true'] { cursor: pointer; }
    tbody tr[data-clickable='true']:hover td { background: ${p => p.theme.table.hover}; }
    tbody tr:focus-visible { outline: 2px solid ${p => p.theme.font.highlight}; outline-offset: -2px; }
    td.main { max-width: 14rem; overflow: hidden; text-overflow: ellipsis; font-weight: 500; }
`

const SortButton = styled.button`
    font: inherit;
    color: inherit;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;

    &:focus-visible { outline: 2px solid ${p => p.theme.font.highlight}; outline-offset: 1px; border-radius: 2px; }
`

const Badge = styled.span`
    display: inline-block;
    padding: 0.05rem 0.5rem;
    border-radius: 999px;
    ${widgetFont.badge}
    font-weight: 600;
    background: ${p => (p.theme.emotive[p.$tone] ?? p.theme.emotive.disabled).bg};
    color: ${p => (p.theme.emotive[p.$tone] ?? p.theme.emotive.disabled).color};
`

const NUMERIC = ['currency', 'integer', 'number', 'decimal', 'percent']

export const relativeTime = (value, locale) => {
    const diff = (new Date(value).getTime() - Date.now()) / 1000
    const abs = Math.abs(diff)
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' })
    if (abs < 60) return rtf.format(Math.round(diff), 'second')
    if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute')
    if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour')
    if (abs < 86400 * 30) return rtf.format(Math.round(diff / 86400), 'day')
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(value))
}

export const statusOf = (statuses = [], row) => statuses.find(s =>
    (!s.condition || evaluateCondition(s.condition, { values: row })) &&
    (!s.pastDue || (row[s.pastDue] && new Date(row[s.pastDue]) < new Date())))

const DataTableWidget = ({ type, data: config, labels }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const w = useAnalyticsWidget(type, config)
    const { period, data, format, locale } = w
    const [ref, size] = useElementSize()
    const table = config.table ?? {}
    const records = !!table.entity
    const list = useEntityRows(records ? [table] : [], period.range)
    const [sort, setSort] = useState(table.sort ?? null)

    const columns = useMemo(() => (table.columns ?? [])
        .filter(c => !c.hideBelow || !size.width || size.width >= c.hideBelow)
        .map(c => ({ ...c, format: c.format ?? (c.value != null ? formatOf(c.value, config.measures) : c.kind === 'name' ? 'text' : 'text') })),
    [table.columns, size.width, config.measures])

    const baseRows = useMemo(() => {
        if (records) return (list.rows[0] ?? []).map(r => ({ id: r.id, record: r }))
        if (!data.current) return []
        const groups = rankGroups(data.current.groups(table.groups?.value), { limit: table.groups?.limit ?? 8, other: false })
        return groups.map(g => ({ id: g.key, group: g, name: groupName(g, table.groups ?? {}, t) }))
    }, [records, list.rows, data.current, table.groups, t])

    const cellValue = (row, col) => {
        if (col.kind === 'name') return row.name
        if (col.value != null) return data.current?.forGroup(col.value, row.group.key)
        return row.record?.[col.field]
    }

    const rows = useMemo(() => {
        if (!sort) return baseRows
        const col = (table.columns ?? []).find(c => c.id === sort.column)
        if (!col) return baseRows
        const dir = sort.direction === 'asc' ? 1 : -1
        return [...baseRows].sort((a, b) => {
            const x = cellValue(a, col), y = cellValue(b, col)
            if (x == null) return 1
            if (y == null) return -1
            return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), locale)) * dir
        })
    }, [baseRows, sort, table.columns, data.current, locale])

    const render = (row, col) => {
        const v = cellValue(row, col)
        switch (col.format) {
            case 'text':     return v == null || v === '' ? '—' : String(v)
            case 'date':     return v ? new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(v)) : '—'
            case 'datetime': return v ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(v)) : '—'
            case 'relative': return v ? <time dateTime={v} title={new Date(v).toLocaleString(locale)}>{relativeTime(v, locale)}</time> : '—'
            case 'status': {
                const s = statusOf(col.statuses, row.record ?? {})
                return s ? <Badge $tone={s.tone}>{t(s.label)}</Badge> : '—'
            }
            default:         return format(v, col.format)
        }
    }

    const open = (row) => table.rowPath && row.id && navigate(`${table.rowPath}/${row.id}`)
    const toggleSort = (col) => setSort(s => ({ column: col.id, direction: s?.column === col.id && s.direction === 'desc' ? 'asc' : 'desc' }))

    const status = records
        ? list.isLoading ? 'loading' : list.error ? 'error' : rows.length ? null : 'empty'
        : w.status

    return (
        <AnalyticsCard
            labels={labels}
            period={period}
            path={config.path}
            status={status}
            error={records ? list.error : data.error}
            onRetry={records ? list.refetch : data.refetch}
            footer={<KpiRow kpis={w.kpis} formatValue={format} />}
        >
            <Scroll ref={ref}>
                <Table>
                    <thead>
                        <tr>
                            {columns.map(col => {
                                const numeric = NUMERIC.includes(col.format)
                                const sorted = sort?.column === col.id
                                return (
                                    <th key={col.id} className={numeric || col.align === 'right' ? 'num' : undefined}
                                        aria-sort={sorted ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}>
                                        {config.sortable === false ? t(col.label) : (
                                            <SortButton type="button" onClick={() => toggleSort(col)}>
                                                {t(col.label)}
                                                {sorted && <span aria-hidden="true">{sort.direction === 'asc' ? '▲' : '▼'}</span>}
                                            </SortButton>
                                        )}
                                    </th>
                                )
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => {
                            const clickable = !!table.rowPath && !!row.id
                            return (
                                <tr key={row.id ?? row.name} data-clickable={clickable} tabIndex={clickable ? 0 : undefined}
                                    onClick={() => open(row)} onKeyDown={e => e.key === 'Enter' && open(row)}>
                                    {columns.map((col, i) => (
                                        <td key={col.id} className={[NUMERIC.includes(col.format) || col.align === 'right' ? 'num' : '', i === 0 ? 'main' : ''].join(' ').trim() || undefined}
                                            title={i === 0 ? String(cellValue(row, col) ?? '') : undefined}>
                                            {render(row, col)}
                                        </td>
                                    ))}
                                </tr>
                            )
                        })}
                    </tbody>
                </Table>
            </Scroll>
        </AnalyticsCard>
    )
}

export default DataTableWidget
